import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import ProjectCard from "./ProjectCard";
import ProjectModal from "./ProjectModal";
import { projects as projectsData } from "@/data/projects";

const ProjectsSection = () => {
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  // Function to get status color classes
  const getStatusStyles = (status) => {
    const normalizedStatus = status?.toLowerCase() || "";

    if (normalizedStatus.includes("progress")) {
      return {
        badgeClass:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800",
        cardBorder: "border-yellow-200 dark:border-yellow-800",
        cardGlow: "shadow-yellow-100 dark:shadow-yellow-900/20",
        cardHover:
          "group-hover:shadow-yellow-200 dark:group-hover:shadow-yellow-800/30",
        icon: "🔄",
      };
    } else if (normalizedStatus === "completed") {
      return {
        badgeClass:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800",
        cardBorder: "border-green-200 dark:border-green-800",
        cardGlow: "shadow-green-100 dark:shadow-green-900/20",
        cardHover:
          "group-hover:shadow-green-200 dark:group-hover:shadow-green-800/30",
        icon: "✅",
      };
    } else if (normalizedStatus === "maintenance") {
      return {
        badgeClass:
          "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800",
        cardBorder: "border-orange-200 dark:border-orange-800",
        cardGlow: "shadow-orange-100 dark:shadow-orange-900/20",
        cardHover:
          "group-hover:shadow-orange-200 dark:group-hover:shadow-orange-800/30",
        icon: "🔧",
      };
    } else {
      return {
        badgeClass:
          "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700",
        cardBorder: "border-gray-200 dark:border-gray-700",
        cardGlow: "shadow-gray-100 dark:shadow-gray-900/20",
        cardHover:
          "group-hover:shadow-gray-200 dark:group-hover:shadow-gray-800/30",
        icon: "📋",
      };
    }
  };

  // Enhanced filter options with better categorization
  const filterOptions = [
    { key: "all", label: "All Projects", count: projectsData.length },
    {
      key: "featured",
      label: "Featured",
      count: projectsData.filter((p) => p.featured).length,
    },
    {
      key: "fullstack",
      label: "Full Stack",
      count: projectsData.filter((p) => p.category === "fullstack").length,
    },
    {
      key: "frontend",
      label: "Frontend",
      count: projectsData.filter(
        (p) => p.category === "frontend" || p.category === "fullstack"
      ).length,
    },
    {
      key: "healthcare",
      label: "Healthcare",
      count: projectsData.filter((p) => p.category === "healthcare").length,
    },
    {
      key: "mobile",
      label: "Mobile Apps",
      count: projectsData.filter((p) => p.category === "mobile").length,
    },
    {
      key: "security",
      label: "Security",
      count: projectsData.filter((p) => p.category === "security").length,
    },
  ];

  // Filter and sort projects
  useEffect(() => {
    let filtered = projectsData.filter((project) => {
      if (filter === "all") return true;
      if (filter === "featured") return project.featured;
      if (filter === "frontend")
        return project.category === "frontend" || project.category === "fullstack";
      return project.category === filter;
    });

    // Sort by priority for better presentation
    filtered = filtered.sort((a, b) => a.priority - b.priority);

    setProjects(filtered);
  }, [filter]);

  // Get displayed projects (3 by default, all when showAll is true)
  const displayedProjects = showAll ? projects : projects.slice(0, 3);
  const hasMoreProjects = projects.length > 3;

  const handleViewMore = () => {
    setShowAll(!showAll);
  };

  // Enhanced animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <section
      id="projects"
      className="section-padding bg-white dark:bg-dark-900 relative overflow-hidden"
      ref={elementRef}
    >
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 opacity-5 dark:opacity-3">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-secondary-500 rounded-full filter blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-3/4 left-1/2 w-64 h-64 bg-accent-400 rounded-full filter blur-2xl animate-pulse delay-2000" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isIntersecting ? "visible" : "hidden"}
          className="max-w-7xl mx-auto"
        >
          {/* Enhanced Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.span
              className="inline-block text-primary-500 font-medium text-lg mb-4 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full"
              variants={itemVariants}
            >
              My Work
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight"
              variants={itemVariants}
            >
              Featured{" "}
              <span className="text-primary-500">
                Projects
              </span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Explore my latest projects showcasing creativity, technical
              expertise, and attention to detail. Each project represents a
              unique challenge and innovative solution.
            </motion.p>
          </motion.div>

          {/* GitHub Link */}
          <motion.div variants={itemVariants} className="flex justify-center mb-16">
            <a
              href="https://github.com/t-boye"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-gray-900 dark:bg-gray-800 hover:bg-gray-700 dark:hover:bg-gray-700 border border-gray-700 dark:border-gray-600 text-white rounded-xl font-medium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
              </svg>
              View my GitHub profile
            </a>
          </motion.div>

          {/* Enhanced Filter Tabs */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {filterOptions.map((option) => (
              <motion.button
                key={option.key}
                onClick={() => {
                  setFilter(option.key);
                  setShowAll(false); // Reset to show 3 projects when filter changes
                }}
                className={`group relative px-6 py-3 rounded-full font-medium transition-all duration-300 overflow-hidden ${
                  filter === option.key
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "bg-gray-100 dark:bg-dark-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-700 hover:shadow-md"
                }`}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10">
                  {option.label}{" "}
                  <span className="opacity-75">({option.count})</span>
                </span>
                {filter === option.key && (
                  <motion.div
                    className="absolute inset-0 bg-primary-600"
                    layoutId="activeFilter"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </motion.div>

          {/* Enhanced Projects Grid */}
          <motion.div
            key={`${filter}-${showAll}`}
            variants={gridVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
          >
            <AnimatePresence mode="sync">
              {displayedProjects.map((project, index) => {
                const statusStyles = getStatusStyles(project.status);

                return (
                  <motion.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                      ease: "easeOut",
                    }}
                    className={`group bg-white dark:bg-dark-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border-2 ${statusStyles.cardBorder} ${statusStyles.cardGlow} hover:${statusStyles.cardHover} hover:-translate-y-2 cursor-pointer`}
                    onClick={() => setSelectedProject(project)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {/* Project Image */}
                    <div className="relative overflow-hidden h-48 bg-gray-100 dark:bg-dark-700">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Status Badge */}
                      <div className="absolute top-4 right-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles.badgeClass} backdrop-blur-sm`}
                        >
                          {statusStyles.icon} {project.status}
                        </span>
                      </div>

                      <div className="absolute top-4 left-4 flex flex-col gap-1">
                        {project.featured && (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full text-xs font-medium border border-yellow-200 dark:border-yellow-800 backdrop-blur-sm">
                            ⭐ Featured
                          </span>
                        )}
                        {project.seekingInvestment && (
                          <span className="px-3 py-1 bg-emerald-500 text-white rounded-full text-xs font-bold backdrop-blur-sm shadow-md">
                            Seeking Investment
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors duration-300 line-clamp-2">
                          {project.title}
                        </h3>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                          {project.year}
                        </span>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                        {project.description}
                      </p>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.slice(0, 3).map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium transition-colors duration-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900 dark:hover:text-primary-300"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium">
                            +{project.technologies.length - 3}
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        {project.liveUrl && (
                          <a
                            href={project.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-primary-500 hover:bg-primary-600 text-white text-center py-2 px-4 rounded-lg font-medium transition-colors duration-300 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Live Demo
                          </a>
                        )}
                        {project.githubUrl && (
                          <a
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 text-center py-2 px-4 rounded-lg font-medium transition-colors duration-300 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Code
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>

          {/* Enhanced View More/Less Button */}
          {hasMoreProjects && (
            <motion.div
              variants={itemVariants}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <motion.button
                onClick={handleViewMore}
                className="group relative px-8 py-4 bg-primary-500 text-white font-semibold rounded-full overflow-hidden transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {showAll ? (
                    <>
                      Show Less
                      <span>↑</span>
                    </>
                  ) : (
                    <>
                      View All Projects ({projects.length - 3} more)
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </>
                  )}
                </span>
                <motion.div className="absolute inset-0 bg-accent-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.button>
            </motion.div>
          )}

          {/* Projects Count Indicator */}
          <motion.div
            className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Showing {displayedProjects.length} of {projects.length} projects
            {filter !== "all" &&
              ` in ${
                filterOptions.find((f) => f.key === filter)?.label
              } category`}
          </motion.div>
        </motion.div>
      </div>

      {/* Enhanced Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
};

export default ProjectsSection;
